// Cash assumptions are separate from the inflation-path generator.
// The monetary response is a transparent scenario rule, not an estimated ECB policy rule.
export const SWR_CASH_MODES = ['inflation-net', 'money-market-lagged', 'fixed', 'macro-proxy'];
export const SWR_CASH_MODEL_VERSION = 'swr-cash-scenarios-v1';
export const SWR_CASH_DEFAULTS = Object.freeze({
  cashAnnualReturn: 0.02,
  cashLongRunReturn: 0.02,
  cashRateHalfLifeMonths: 12,
  cashInflationPassThrough: 0.5,
});

export function resolveSWRCashMode(params = {}) {
  if (params.cashReturnMode != null) return params.cashReturnMode;
  if (params.swrConfigurationMode === 'standard') return 'inflation-net';
  if (params.swrConfigurationMode === 'advanced') return 'money-market-lagged';
  // Preserve explicit assumptions in saved v5/API inputs; new simple inputs use net indexation.
  if (params.macroMode === 'stochastic-euro-v1') return 'macro-proxy';
  return params.cashAnnualReturn != null ? 'fixed' : 'inflation-net';
}

export function describeSWRCashModel(params = {}) {
  const mode = resolveSWRCashMode(params);
  if (mode === 'inflation-net') return 'Rendimento netto pari all’inflazione: potere d’acquisto costante per ipotesi. Costi e imposte del rendimento sono già assorbiti; nessuna seconda tassazione.';
  if (mode === 'money-market-lagged') return 'Fondo monetario: tasso iniziale e di lungo periodo ipotizzati, risposta graduale e parziale all’inflazione osservata nei 12 mesi precedenti. Può perdere potere d’acquisto; non è una previsione della BCE.';
  if (mode === 'macro-proxy') return 'Proxy storica dei depositi dell’area euro fino a un anno. È il precedente modello macro, non la replica di un fondo monetario.';
  return 'Rendimento nominale costante, al netto dei costi del fondo e prima delle imposte. Non aumenta automaticamente durante una fiammata inflattiva.';
}

export function buildSWRCashRates(params, inflationMonthlyRates, legacyGrossRates) {
  const mode = resolveSWRCashMode(params);
  if (!SWR_CASH_MODES.includes(mode)) throw new Error('Modello di rendimento del buffer non valido.');
  const grossRates = new Float64Array(inflationMonthlyRates.length);
  const netRates = new Float64Array(inflationMonthlyRates.length);
  const taxRate = params.cashTaxRate ?? 0.26;
  const initialRate = params.cashAnnualReturn ?? SWR_CASH_DEFAULTS.cashAnnualReturn;
  const longRunRate = params.cashLongRunReturn ?? SWR_CASH_DEFAULTS.cashLongRunReturn;
  const halfLife = params.cashRateHalfLifeMonths ?? SWR_CASH_DEFAULTS.cashRateHalfLifeMonths;
  const passThrough = params.cashInflationPassThrough ?? SWR_CASH_DEFAULTS.cashInflationPassThrough;
  const baseInflationLog = Math.log1p(params.inflationRate ?? 0) / 12;
  const longRunLog = Math.log1p(longRunRate) / 12;
  let rateLog = Math.log1p(initialRate) / 12;
  const adjustment = -Math.expm1(-Math.LN2 / halfLife);
  // Unknown prehistory is initialized at the user long-run inflation assumption.
  const observedLogs = new Float64Array(12).fill(baseInflationLog);
  let sumObserved = baseInflationLog * 12;

  for (let month = 0; month < inflationMonthlyRates.length; month++) {
    let gross;
    if (mode === 'inflation-net') gross = inflationMonthlyRates[month];
    else if (mode === 'macro-proxy') gross = legacyGrossRates[month];
    else gross = Math.expm1(rateLog);
    grossRates[month] = gross;
    netRates[month] = mode === 'inflation-net' ? gross : gross - Math.max(gross, 0) * taxRate;

    if (mode === 'money-market-lagged') {
      // Today's cash rate cannot react to inflation that has not yet occurred.
      const slot = month % 12;
      const observed = Math.log1p(inflationMonthlyRates[month]);
      sumObserved += observed - observedLogs[slot];
      observedLogs[slot] = observed;
      const targetLog = longRunLog + passThrough * (sumObserved / 12 - baseInflationLog);
      rateLog += adjustment * (targetLog - rateLog);
    }
  }
  return { grossRates, netRates };
}
